<template>
    <div class="px-3 sm:px-4 py-3 flex-shrink-0 border-t border-gray-200 bg-white">
        <div
            class="items-end rounded-lg border border-[#ACACAC] py-2 px-3 sm:px-4 flex flex-col lg:flex-row gap-2 lg:gap-0">
            <div class="min-w-0 flex-1 w-full">
                <div
                    class="max-h-[150px] sm:max-h-[200px] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    <div ref="messageInput"
                        class="min-h-[40px] pt-2 pl-2 outline-none [&:focus>span]:hidden resize-none w-full max-w-full break-words whitespace-pre-wrap text-sm sm:text-base"
                        contenteditable @input="handleInput" @keydown.enter="handleKeyDown" @paste="handlePaste">
                        <span v-show="showPlaceholder" class="text-[#A0A0A0] pointer-events-none">
                            {{ placeholder }}
                        </span>
                    </div>

                    <div v-if="attachments.length" class="mt-3 space-y-2 text-xs sm:text-sm text-gray-700">
                        <div v-for="(attachment, index) in attachments" :key="attachment.id"
                            class="flex items-center justify-between gap-2 sm:gap-3 rounded-lg border border-gray-200 bg-gray-100 px-2 sm:px-3 py-2">
                            <div class="flex min-w-0 items-center gap-2">
                                <img v-if="attachment.previewUrl" :src="attachment.previewUrl"
                                    class="h-10 w-10 sm:h-12 sm:w-12 rounded-lg object-cover flex-shrink-0"
                                    alt="Pré-visualização do anexo" />
                                <font-awesome-icon v-else icon="fa-paperclip" class="text-gray-500 flex-shrink-0" />
                                <span class="truncate" :title="attachment.name">{{ attachment.shortName }}</span>
                            </div>
                            <button type="button"
                                class="text-xs text-red-500 hover:text-red-600 flex-shrink-0 whitespace-nowrap"
                                @click="removeAttachment(index)">
                                Remover
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-center justify-end gap-2 self-end w-full lg:w-auto">
                <button type="button" class="h-10 w-10 text-lg sm:text-xl text-[#868D96] align-middle flex-shrink-0"
                    @click="openFileDialog()">
                    <font-awesome-icon class="pointer-events-none" icon="fa-paperclip" />
                </button>

                <button type="button"
                    class="btn-purple flex h-10 w-10 items-center justify-center cursor-pointer flex-shrink-0"
                    @click="handleSend" :disabled="!canSend || sending">
                    <span class="hidden">Enviar</span>
                    <font-awesome-icon v-if="sending" icon="fa-spinner" class="pointer-events-none animate-spin" />
                    <font-awesome-icon v-else class="pointer-events-none" icon="fa-paper-plane" />
                </button>
            </div>
        </div>
        <input ref="fileInput" type="file" class="hidden" :accept="acceptedFileTypes" multiple
            @change="handleFileChange" />
    </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from 'vue'

const shortenFileName = (name = '', maxLength = 32) => {
    if (!name) return ''
    if (name.length <= maxLength) return name

    const lastDotIndex = name.lastIndexOf('.')
    const extension = lastDotIndex > 0 ? name.slice(lastDotIndex) : ''
    const baseName = lastDotIndex > 0 ? name.slice(0, lastDotIndex) : name
    const baseMaxLength = Math.max(maxLength - extension.length - 3, 1)

    return `${baseName.slice(0, baseMaxLength)}...${extension}`
}

export default {
    props: {
        placeholder: {
            type: String,
            default: 'Escreva sua mensagem'
        },
        sending: {
            type: Boolean,
            default: false
        },
        acceptedFileTypes: {
            type: String,
            default: 'image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt'
        },
        maxFileSizeMb: {
            type: Number,
            default: 10
        }
    },

    emits: ['send', 'update:modelValue'],

    setup(props, { emit }) {
        const messageInput = ref(null)
        const fileInput = ref(null)
        const message = ref('')
        const attachments = ref([])

        const showPlaceholder = computed(() => {
            return !message.value.length && attachments.value.length === 0
        })

        const canSend = computed(() => {
            if (props.sending) return false
            const hasMessage = message.value && message.value.trim().length
            const hasAttachments = attachments.value.length > 0
            return hasMessage || hasAttachments
        })

        const handleInput = (event) => {
            message.value = event.target.innerText
            emit('update:modelValue', message.value)
        }

        const handleKeyDown = (event) => {
            // No desktop: Enter envia, Shift+Enter quebra linha
            // No mobile: Enter sempre quebra linha, só envia com botão
            if (event.key === 'Enter') {
                // Detecta se é mobile
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

                if (!isMobile && !event.shiftKey) {
                    // Desktop: Enter sem Shift envia
                    event.preventDefault()
                    handleSend()
                } else if (!isMobile && event.shiftKey) {
                    // Desktop: Shift+Enter quebra linha (comportamento padrão)
                    // Não faz nada, deixa o comportamento padrão
                }
                // Mobile: Deixa Enter quebrar linha normalmente
            }
        }

        const handlePaste = (event) => {
            // Previne formatação ao colar texto
            event.preventDefault()
            const text = event.clipboardData.getData('text/plain')
            document.execCommand('insertText', false, text)
        }

        const handleSend = () => {
            if (!canSend.value) return

            const text = message.value.trim()
            const files = attachments.value

            emit('send', { message: text, attachments: files })

            // Limpa após enviar
            clearInput()
        }

        const clearInput = () => {
            message.value = ''
            if (messageInput.value) {
                messageInput.value.innerText = ''
            }
            resetAttachments()
        }

        const openFileDialog = () => {
            if (!fileInput.value) return
            fileInput.value.click()
        }

        const handleFileChange = (event) => {
            const input = event.target
            const files = Array.from(input?.files || [])

            if (!files.length) return

            const maxBytes = props.maxFileSizeMb * 1024 * 1024
            const rejectedFiles = []

            files.forEach((file, fileIndex) => {
                if (file.size > maxBytes) {
                    rejectedFiles.push(file.name)
                    return
                }

                const previewUrl = file.type && file.type.startsWith('image/')
                    ? URL.createObjectURL(file)
                    : ''

                const attachmentId = window.crypto?.randomUUID?.()
                    || `att-${Date.now()}-${fileIndex}-${Math.random().toString(16).slice(2)}`

                attachments.value.push({
                    id: attachmentId,
                    file,
                    name: file.name,
                    shortName: shortenFileName(file.name, 32),
                    previewUrl,
                })
            })

            if (rejectedFiles.length) {
                const formatted = rejectedFiles.join(', ')
                alert(`Os arquivos a seguir excedem o limite de ${props.maxFileSizeMb}MB e foram ignorados: ${formatted}`)
            }

            if (fileInput.value) {
                fileInput.value.value = ''
            }
        }

        const removeAttachment = (index) => {
            const [removed] = attachments.value.splice(index, 1)
            if (removed?.previewUrl) {
                URL.revokeObjectURL(removed.previewUrl)
            }
        }

        const resetAttachments = () => {
            attachments.value.forEach(attachment => {
                if (attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl)
                }
            })
            attachments.value = []
            if (fileInput.value) {
                fileInput.value.value = ''
            }
        }

        onBeforeUnmount(() => {
            resetAttachments()
        })

        return {
            messageInput,
            fileInput,
            message,
            attachments,
            showPlaceholder,
            canSend,
            handleInput,
            handleKeyDown,
            handlePaste,
            handleSend,
            openFileDialog,
            handleFileChange,
            removeAttachment,
            clearInput
        }
    }
}
</script>

<style scoped>
/* Melhorias para contenteditable em mobile */
[contenteditable] {
    -webkit-user-select: text;
    user-select: text;
    -webkit-tap-highlight-color: transparent;
}

[contenteditable]:focus {
    outline: none;
}

/* Previne zoom em iOS quando foca no input */
@media screen and (max-width: 640px) {
    [contenteditable] {
        font-size: 16px !important;
    }
}

/* Garante que o contenteditable se comporte bem em mobile */
[contenteditable]:empty:before {
    content: attr(data-placeholder);
    color: #A0A0A0;
    pointer-events: none;
}
</style>
